import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  getDramaPerformance,
  DramaPerformanceItem,
} from "@/services/firebase/revenue";
import { exportToCSV } from "@/utils/csvExport";
import { Download, Eye, Building2 } from "lucide-react";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { AdminDataTable } from "@/components/admin-ui/admin-data-table";
import type { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { AdminLoadingSkeleton } from "@/components/admin-ui/admin-loading-skeleton";
import { AdminErrorAlert } from "@/components/admin-ui/admin-error-alert";
import { AdminEmptyState } from "@/components/admin-ui/admin-empty-state";
import { AdminStatsCard } from "@/components/admin-ui/admin-stats-card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import FinancialDateRangePicker from "@/components/financial/FinancialDateRangePicker";
import { useFinancialDateRange } from "@/store/financialDateRange";

interface CompanyAggregate {
  company_id: string;
  company_name: string;
  drama_count: number;
  watcher_count: number;
  unlock_count: number;
  full_drama_count: number;
  paid_coins: number;
  free_coins: number;
  total_coins: number;
  unique_unlock_users: number;
  calculated_amount: number;
  platform_revenue_ntd: number;
}

const columns: AdminTableColumn[] = [
  { key: "company_name", label: "片商", sortable: true },
  { key: "drama_count", label: "劇集數", sortable: true },
  { key: "watcher_count", label: "觀看用戶", sortable: true },
  { key: "unlock_count", label: "解鎖次數", sortable: true },
  { key: "full_drama_count", label: "全劇購買", sortable: true, hideBelow: "lg" },
  { key: "total_coins", label: "總金幣", sortable: true },
  { key: "paid_coins", label: "Paid", sortable: true },
  { key: "free_coins", label: "Free", sortable: true },
  { key: "unique_unlock_users", label: "付費人數", sortable: true, hideBelow: "md" },
  { key: "calculated_amount", label: "片商分潤(NTD)", sortable: true },
  { key: "platform_revenue_ntd", label: "平台收益(NTD)", sortable: true },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const UNASSIGNED_KEY = "__unassigned__";

export default function CompanyPerformancePage() {
  const navigate = useNavigate();
  const { start: startDate, end: endDate } = useFinancialDateRange();
  const [pageSize, setPageSize] = useState<number>(25);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("all");

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["drama-performance", startDate, endDate],
    queryFn: () => getDramaPerformance({ startDate, endDate }),
    staleTime: 30_000,
  });

  // 聚合 drama → company
  const companies = useMemo<CompanyAggregate[]>(() => {
    if (!data) return [];
    const map = new Map<string, CompanyAggregate>();
    for (const d of data.dramas as DramaPerformanceItem[]) {
      const cid = d.company_id || UNASSIGNED_KEY;
      const cname = d.company_name || (cid === UNASSIGNED_KEY ? "(未分派)" : cid);
      let agg = map.get(cid);
      if (!agg) {
        agg = {
          company_id: cid,
          company_name: cname,
          drama_count: 0,
          watcher_count: 0,
          unlock_count: 0,
          full_drama_count: 0,
          paid_coins: 0,
          free_coins: 0,
          total_coins: 0,
          unique_unlock_users: 0,
          calculated_amount: 0,
          platform_revenue_ntd: 0,
        };
        map.set(cid, agg);
      }
      agg.drama_count += 1;
      agg.watcher_count += d.watcher_count;
      agg.unlock_count += d.unlock_count;
      agg.full_drama_count += d.full_drama_count;
      agg.paid_coins += d.paid_coins;
      agg.free_coins += d.free_coins;
      agg.total_coins += d.total_coins;
      agg.unique_unlock_users += d.unique_unlock_users;
      agg.calculated_amount += d.calculated_amount;
      agg.platform_revenue_ntd += d.platform_revenue_ntd;
    }
    return [...map.values()];
  }, [data]);

  const filteredCompanies = useMemo<CompanyAggregate[]>(() => {
    if (selectedCompanyId === "all") return companies;
    return companies.filter((c) => c.company_id === selectedCompanyId);
  }, [companies, selectedCompanyId]);

  const totals = useMemo(() => {
    if (!filteredCompanies.length) return null;
    return filteredCompanies.reduce(
      (acc, c) => ({
        drama_count: acc.drama_count + c.drama_count,
        watcher_count: acc.watcher_count + c.watcher_count,
        unlock_count: acc.unlock_count + c.unlock_count,
        full_drama_count: acc.full_drama_count + c.full_drama_count,
        calculated_amount: acc.calculated_amount + c.calculated_amount,
        platform_revenue_ntd: acc.platform_revenue_ntd + c.platform_revenue_ntd,
      }),
      {
        drama_count: 0,
        watcher_count: 0,
        unlock_count: 0,
        full_drama_count: 0,
        calculated_amount: 0,
        platform_revenue_ntd: 0,
      }
    );
  }, [filteredCompanies]);

  const handleExport = () => {
    if (!filteredCompanies.length) return;
    const rows = filteredCompanies.map((c) => ({
      片商: c.company_name,
      片商ID: c.company_id,
      劇集數: c.drama_count,
      觀看用戶: c.watcher_count,
      解鎖次數: c.unlock_count,
      全劇購買: c.full_drama_count,
      總金幣: c.total_coins,
      Paid: c.paid_coins,
      Free: c.free_coins,
      付費人數: c.unique_unlock_users,
      片商分潤NTD: c.calculated_amount,
      平台收益NTD: c.platform_revenue_ntd,
    }));
    exportToCSV(
      rows,
      [
        { key: "片商" as const, label: "片商" },
        { key: "片商ID" as const, label: "片商 ID" },
        { key: "劇集數" as const, label: "劇集數" },
        { key: "觀看用戶" as const, label: "觀看用戶" },
        { key: "解鎖次數" as const, label: "解鎖次數" },
        { key: "全劇購買" as const, label: "全劇購買" },
        { key: "總金幣" as const, label: "總金幣" },
        { key: "Paid" as const, label: "Paid" },
        { key: "Free" as const, label: "Free" },
        { key: "付費人數" as const, label: "付費人數" },
        { key: "片商分潤NTD" as const, label: "片商分潤 (NTD)" },
        { key: "平台收益NTD" as const, label: "平台收益 (NTD)" },
      ],
      `company_performance_${startDate}_${endDate}`
    );
  };

  return (
    <AdminPage>
      <AdminPageHeader
        title="片商表現"
        subtitle="指定期間每個片商的解鎖、金幣消費與分潤合計"
        loading={isLoading}
        onRefresh={() => refetch()}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={!filteredCompanies.length}
          >
            <Download className="h-4 w-4 mr-1" />
            CSV 匯出
          </Button>
        }
      />

      {/* 日期 + 片商 + 每頁筆數 */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <FinancialDateRangePicker mode="range" />
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            片商
          </label>
          <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="全部片商" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部片商</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.company_id} value={c.company_id}>
                  {c.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto">
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            每頁筆數
          </label>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && <AdminErrorAlert message={`載入失敗: ${(error as Error).message}`} />}

      {(isLoading || isFetching) && !data ? (
        <AdminLoadingSkeleton variant="mixed" />
      ) : !data ? null : (
        <div className="space-y-6">
          {/* 總覽卡片 */}
          {totals && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <AdminStatsCard
                icon={<Building2 className="h-4 w-4" />}
                label="片商數"
                value={filteredCompanies.length.toLocaleString()}
              />
              <AdminStatsCard
                icon={<Eye className="h-4 w-4" />}
                label="觀看用戶總人次"
                value={totals.watcher_count.toLocaleString()}
              />
              <AdminStatsCard
                icon={<Building2 className="h-4 w-4" />}
                label="總解鎖次數"
                value={(totals.unlock_count + totals.full_drama_count).toLocaleString()}
              />
              <AdminStatsCard
                icon={<Building2 className="h-4 w-4" />}
                label="片商分潤合計"
                value={`NTD ${totals.calculated_amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              />
              <AdminStatsCard
                icon={<Building2 className="h-4 w-4" />}
                label="平台收益合計"
                value={`NTD ${totals.platform_revenue_ntd.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              />
            </div>
          )}

          {/* 表格 */}
          {filteredCompanies.length > 0 ? (
            <AdminDataTable
              columns={columns}
              data={filteredCompanies}
              pageSize={pageSize}
              defaultSort={{ key: "calculated_amount", dir: "desc" }}
              getRowId={(item) => item.company_id}
              onRowClick={(item) => {
                if (item.company_id !== UNASSIGNED_KEY) {
                  navigate(`/financial/revenue/company/${item.company_id}?start=${startDate}&end=${endDate}`);
                }
              }}
              renderCell={(item: CompanyAggregate, col) => {
                switch (col.key) {
                  case "company_name":
                    return (
                      <span className="font-medium text-foreground">
                        {item.company_name}
                      </span>
                    );
                  case "drama_count":
                    return (
                      <span className="text-right block tabular-nums">
                        {item.drama_count.toLocaleString()}
                      </span>
                    );
                  case "watcher_count":
                  case "unlock_count":
                  case "full_drama_count":
                  case "total_coins":
                  case "paid_coins":
                  case "free_coins":
                  case "unique_unlock_users":
                    return (
                      <span className="text-right block tabular-nums">
                        {(item as any)[col.key].toLocaleString()}
                      </span>
                    );
                  case "calculated_amount":
                    return (
                      <span className="text-right block tabular-nums font-medium text-amber-700 dark:text-amber-400">
                        NTD {item.calculated_amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    );
                  case "platform_revenue_ntd":
                    return (
                      <span
                        className={`text-right block tabular-nums font-medium ${
                          item.platform_revenue_ntd >= 0
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-rose-600 dark:text-rose-400"
                        }`}
                      >
                        NTD {item.platform_revenue_ntd.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    );
                  default:
                    return null;
                }
              }}
            />
          ) : (
            <AdminEmptyState title="此期間暫無片商表現資料" />
          )}

          <div className="text-xs text-muted-foreground space-y-1 border-t pt-4">
            <p>
              <strong>聚合方式：</strong>「片商表現」由「劇集表現」依 <code>company_id</code> 聚合而來，數值與劇集表現的合計一致。
            </p>
            <p>
              <strong>點擊列：</strong>進入該片商的營收詳情頁面（含每劇明細）。
            </p>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
